import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import './CookieConsent.css';

const STORAGE_KEY = 'ufficient-cookie-consent';
const SESSION_DISMISS_KEY = 'ufficient-cookie-banner-dismissed';
const CONSENT_VERSION = 1;

const defaultCategories = { analytics: false, marketing: false };

const readStoredConsent = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed.version !== CONSENT_VERSION) return null;
        return parsed;
    } catch {
        return null;
    }
};

const writeStoredConsent = (categories) => {
    const consent = {
        version: CONSENT_VERSION,
        necessary: true,
        analytics: categories.analytics,
        marketing: categories.marketing,
        updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    window.dispatchEvent(new CustomEvent('ufficient:consent-change', { detail: consent }));
    return consent;
};

window.getUfficientConsent = () => readStoredConsent();

const categoryList = [
    {
        key: 'necessary',
        title: 'Strictly necessary',
        description: 'Required for core site functionality and security. Always on.',
        locked: true,
    },
    {
        key: 'analytics',
        title: 'Analytics',
        description: 'Helps us understand how the site is used so we can improve it.',
    },
    {
        key: 'marketing',
        title: 'Marketing',
        description: 'Used to measure the effectiveness of our campaigns.',
    },
];

const CookieConsent = () => {
    const [visible, setVisible] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [categories, setCategories] = useState(defaultCategories);

    useEffect(() => {
        const stored = readStoredConsent();
        if (stored) {
            setCategories({ analytics: stored.analytics, marketing: stored.marketing });
        }

        const dismissedThisSession = sessionStorage.getItem(SESSION_DISMISS_KEY);
        if (!stored && !dismissedThisSession) {
            setVisible(true);
        }

        window.openCookieSettings = () => {
            const current = readStoredConsent();
            setCategories(current ? { analytics: current.analytics, marketing: current.marketing } : defaultCategories);
            setExpanded(true);
            setVisible(true);
        };

        return () => {
            delete window.openCookieSettings;
        };
    }, []);

    const closeBanner = useCallback(() => {
        setVisible(false);
        setExpanded(false);
    }, []);

    const acceptAll = () => {
        writeStoredConsent({ analytics: true, marketing: true });
        setCategories({ analytics: true, marketing: true });
        closeBanner();
    };

    const rejectNonEssential = () => {
        writeStoredConsent({ analytics: false, marketing: false });
        setCategories({ analytics: false, marketing: false });
        closeBanner();
    };

    const savePreferences = () => {
        writeStoredConsent(categories);
        closeBanner();
    };

    const dismissWithoutDeciding = () => {
        sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
        closeBanner();
    };

    const toggleCategory = (key) => {
        setCategories((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    className="cookie-consent"
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 40 }}
                    transition={{ duration: 0.3 }}
                    role="dialog"
                    aria-label="Cookie preferences"
                >
                    <div className="cookie-consent-main">
                        <div className="cookie-consent-body">
                            <p>
                                We use cookies to run essential site functions and, with your permission, to understand how the site is used.
                                See our <a href="/cookie-policy">Cookie Policy</a> for details.
                            </p>

                            {expanded && (
                                <div className="cookie-categories">
                                    {categoryList.map((category) => (
                                        <div className="cookie-category" key={category.key}>
                                            <div className="cookie-category-info">
                                                <h3>{category.title}</h3>
                                                <p>{category.description}</p>
                                            </div>
                                            <button
                                                type="button"
                                                className={`cookie-toggle ${category.locked || categories[category.key] ? 'on' : ''}`}
                                                role="switch"
                                                aria-checked={category.locked ? true : categories[category.key]}
                                                aria-label={category.title}
                                                disabled={category.locked}
                                                onClick={() => !category.locked && toggleCategory(category.key)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="cookie-actions">
                                <button className="cookie-btn accept" onClick={acceptAll}>Accept All</button>
                                <button className="cookie-btn decline" onClick={rejectNonEssential}>Reject Non-Essential</button>
                                {expanded ? (
                                    <button className="cookie-btn customize" onClick={savePreferences}>Save Preferences</button>
                                ) : (
                                    <button className="cookie-btn customize" onClick={() => setExpanded(true)}>Customize</button>
                                )}
                            </div>
                        </div>

                        <button className="cookie-close" onClick={dismissWithoutDeciding} aria-label="Dismiss">
                            <X size={18} />
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default CookieConsent;
